/**
 * The Glass — the catalog pane (wave glass-6, #805; THE GLASS ruled #786).
 *
 * A sub-route of The Glass: catalog-lookup in the browser — client-side
 * search over the committed engineering/contract/catalog.generated.json,
 * packaged verbatim and whole into the glass canon at build time. Already
 * fed (design-786 §4 row 6): the CI regenerate-and-compare is the feeder;
 * this pane reads NO live system at render time.
 *
 * The glass constitution (design-786 §2.10) governs: render canon, never
 * author state — the pane's description of the Catalog is the file's own
 * what_this_is, verbatim; the coverage boundary renders from the packaged
 * card code-path:catalog_lookup's text field. Every card wears its stamp
 * as the canon carries it; freshness displayed, never auto-judged. The
 * search reproduces the lookup verb's resolution order (catalog-search.ts);
 * register lines render from the already-packaged register rows (glass-3).
 * The displayed_on marker and the absence-is-a-signal no-hit text are the
 * script's own lines, carried as it prints them.
 *
 * The search box's placeholder names no kinds (#1008, decided and signed by
 * Claude, PM 2.0, 2026-10-04). Until then it listed six kinds of name (#805)
 * — an authored statement of what the Catalog holds, which went stale as
 * cron duties, communications and other kinds were added.
 * It now points at what this page renders from canon: the tally of cards by
 * kind below the box (empty query) and the boundary card's line above it.
 */

import { useMemo, useState } from "react";
import {
  GLASS_CANON,
  type GlassCatalogCard,
  type GlassCatalogEdge,
  type GlassRegisterRow,
} from "@command-center/shared";
import {
  LIST_CAP,
  NARROW_THRESHOLD,
  avoidHit,
  buildEdgeIndex,
  cardRegisterPaths,
  groupEdgesByType,
  matchCards,
  oneLine,
  registerFallback,
  registerLines,
  type EdgeIndex,
} from "./catalog-search";

/** The packaged card whose text carries the Catalog's coverage boundary. */
const BOUNDARY_CARD_ID = "code-path:catalog_lookup";

export function GlassCatalogPage() {
  const { catalog } = GLASS_CANON;
  const [query, setQuery] = useState("");

  const index = useMemo(() => buildEdgeIndex(catalog), [catalog]);
  const rowsByPath = useMemo(() => {
    const m = new Map<string, GlassRegisterRow>();
    for (const r of GLASS_CANON.register.rows) m.set(r.path, r);
    return m;
  }, []);

  const hits = useMemo(
    () => matchCards(catalog.cards, query),
    [catalog.cards, query],
  );
  const trimmed = query.trim();
  const boundaryCard = useMemo(
    () => catalog.cards.find((c) => c.id === BOUNDARY_CARD_ID),
    [catalog.cards],
  );

  return (
    <section className="mx-auto max-w-6xl p-6">
      <h1 className="mb-1 text-xl tracking-tight">The Glass — Catalog</h1>
      <p className="mb-1 text-sm">{catalog.what_this_is}</p>
      <p className="mb-1 text-xs text-plane-muted">
        renders <code>{GLASS_CANON.meta.sources.catalog}</code> ({catalog.counts.cards}{" "}
        cards · {catalog.counts.edges} edges), packaged at build time; this
        page searches it read-only, reproducing{" "}
        <code>engineering/contract/bin/catalog-lookup.py</code>'s resolution
        order client-side.
      </p>
      {boundaryCard !== undefined ? (
        <p className="mb-5 text-xs" data-testid="glass-catalog-boundary">
          <span className="text-plane-muted">
            renders card <code>{BOUNDARY_CARD_ID}</code> text:{" "}
          </span>
          {boundaryCard.text}
        </p>
      ) : null}

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="query any name — the kinds the Catalog holds are tallied below; where its index ends is the line above"
        className="mb-6 w-full rounded-md border border-border bg-card px-3 py-2 text-sm"
        data-testid="glass-catalog-query"
        autoFocus
      />

      {trimmed === "" ? (
        <KindTally cards={catalog.cards} />
      ) : hits.length === 0 ? (
        <NoHit query={trimmed} rowsByPath={rowsByPath} />
      ) : hits.length > NARROW_THRESHOLD ? (
        <NarrowList query={trimmed} hits={hits} />
      ) : (
        <>
          {hits.map((c) => (
            <CatalogCard
              key={c.id}
              card={c}
              query={trimmed}
              index={index}
              rowsByPath={rowsByPath}
            />
          ))}
          {catalog.displayed_on.status !== "declared" ? (
            <p className="mt-4 text-xs text-plane-muted">
              (displayed-on: not extracted — no surface declares its display
              yet; ruled 2026-08-20, see catalog.generated.json displayed_on)
            </p>
          ) : null}
        </>
      )}
    </section>
  );
}

/** The empty-query state: the card tally by kind — counts measured from the
 *  packaged canon at render, never hardcoded. */
function KindTally({ cards }: { cards: readonly GlassCatalogCard[] }) {
  const tally = new Map<string, number>();
  for (const c of cards) tally.set(c.kind, (tally.get(c.kind) ?? 0) + 1);
  return (
    <div className="rounded-md border border-border bg-card p-4 text-xs">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-plane-muted">
        Cards by kind
      </div>
      <ul className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
        {[...tally.entries()]
          .sort(([, a], [, b]) => b - a)
          .map(([kind, n]) => (
            <li key={kind}>
              <code>{kind}</code>: {n}
            </li>
          ))}
      </ul>
    </div>
  );
}

/** The script's no-hit output: the register fallback where paths answer,
 *  else the absence-is-a-signal lines, verbatim. */
function NoHit({
  query,
  rowsByPath,
}: {
  query: string;
  rowsByPath: ReadonlyMap<string, GlassRegisterRow>;
}) {
  const regHits = registerFallback(query, rowsByPath);
  if (regHits.length > 0) {
    return (
      <div className="rounded-md border border-border bg-card p-4 text-xs">
        <p className="mb-2">
          no Catalog card, but the register answers "{query}":
        </p>
        <RegisterLineList
          lines={registerLines(regHits, rowsByPath)}
        />
      </div>
    );
  }
  return (
    <div
      className="rounded-md border border-border bg-card p-4 text-xs"
      data-testid="glass-catalog-nohit"
    >
      <p>nothing in the Catalog answers "{query}".</p>
      <p className="mt-2 text-plane-muted">
        That absence is a signal: ask the term guide (glossary.yaml
        term_guide) before coining — a candidate is used and marked (the
        orientation contract, engineering/docs/agents/brief.md).
      </p>
    </div>
  );
}

/** The script's &gt;8-hits output: id, stamp, first line of text — narrow
 *  the name. */
function NarrowList({
  query,
  hits,
}: {
  query: string;
  hits: readonly GlassCatalogCard[];
}) {
  return (
    <div className="rounded-md border border-border bg-card p-4 text-xs">
      <p className="mb-2">
        {hits.length} cards match "{query}" — narrow the name:
      </p>
      <ul className="space-y-1">
        {hits.slice(0, LIST_CAP).map((c) => (
          <li key={c.id}>
            <code>{c.id}</code>{" "}
            <span className="text-plane-muted">({c.stamp})</span>{" "}
            {oneLine(c.text, 90)}
          </li>
        ))}
      </ul>
      {hits.length > LIST_CAP ? (
        <p className="mt-2 text-plane-muted">
          … and {hits.length - LIST_CAP} more — narrow further.
        </p>
      ) : null}
    </div>
  );
}

/** One card plus its one-hop neighborhood — the script's print_card(),
 *  values verbatim. */
function CatalogCard({
  card: c,
  query,
  index,
  rowsByPath,
}: {
  card: GlassCatalogCard;
  query: string;
  index: EdgeIndex;
  rowsByPath: ReadonlyMap<string, GlassRegisterRow>;
}) {
  const outgoing = index.byFrom.get(c.id) ?? [];
  const incoming = index.byTo.get(c.id) ?? [];
  const regLines = registerLines(
    cardRegisterPaths(c).filter((p) => rowsByPath.has(p)),
    rowsByPath,
  );
  const bareName = c.id.includes(":") ? c.id.slice(c.id.indexOf(":") + 1) : c.id;
  const avoided = avoidHit(c, query);

  return (
    <div
      className="mb-4 rounded-md border border-border bg-card p-4 text-xs"
      data-testid={`glass-catalog-card-${c.id}`}
    >
      <div className="mb-1 font-medium">
        <code className="text-sm">{c.id}</code>
      </div>
      <dl className="mb-2 space-y-0.5">
        <MetaField label="kind" value={c.kind} extra={`stamp: ${c.stamp.toUpperCase()}`} />
        {c.name !== bareName ? <MetaField label="name" value={c.name} /> : null}
        {c.aliases.length > 0 ? (
          <MetaField label="aliases" value={c.aliases.join(", ")} />
        ) : null}
        {c.ref !== undefined ? <MetaField label="ref" value={c.ref} /> : null}
        {c.files !== undefined && c.files.length > 0 ? (
          <MetaField label="files" value={c.files.join(", ")} />
        ) : null}
        {c.class !== undefined ? (
          <MetaField
            label="class"
            value={c.class}
            extra={c.type !== undefined ? `type: ${c.type}` : undefined}
          />
        ) : null}
        {c.grain !== undefined ? (
          <MetaField label="grain" value={oneLine(c.grain, 300)} />
        ) : null}
      </dl>
      <p className="mb-2 whitespace-pre-wrap">
        {c.text !== "" ? c.text : "(no ruled text)"}
      </p>
      {c.avoid !== undefined && c.avoid.length > 0 ? (
        <p className="mb-2 font-medium">AVOID: {c.avoid.join(", ")}</p>
      ) : null}
      {avoided !== undefined ? (
        <p className="mb-2 font-medium" data-testid="glass-catalog-avoid-hit">
          "{avoided}" is on this term's avoid list (glossary.yaml) — not an
          alias. The ratified term is {c.name}.
        </p>
      ) : null}
      {c.flags !== undefined
        ? c.flags.map((f, i) => (
            <p key={i} className="mb-1 font-medium">
              FLAG: {oneLine(String(f), 300)}
            </p>
          ))
        : null}
      <p className="mb-2 text-plane-muted">source: {c.source}</p>

      {outgoing.length === 0 && incoming.length === 0 ? (
        <p className="text-plane-muted">edges: none recorded</p>
      ) : null}
      <EdgeGroups label="→" edges={outgoing} end="to" index={index} />
      <EdgeGroups label="←" edges={incoming} end="from" index={index} />

      {regLines.length > 0 ? (
        <div className="mt-2 border-t border-border pt-2">
          <RegisterLineList lines={regLines} />
        </div>
      ) : null}
    </div>
  );
}

/** One direction's edges grouped by type — neighbor id, stamp, one line of
 *  its text, the cite; the 40-per-type cap with the script's overflow line. */
function EdgeGroups({
  label,
  edges,
  end,
  index,
}: {
  label: string;
  edges: readonly GlassCatalogEdge[];
  end: "to" | "from";
  index: EdgeIndex;
}) {
  if (edges.length === 0) return null;
  return (
    <>
      {groupEdgesByType(edges).map(([etype, list]) => (
        <div key={`${label}-${etype}`} className="mb-1">
          <div className="font-medium">
            {label} {etype}:
          </div>
          <ul className="ml-4 space-y-0.5">
            {list.slice(0, LIST_CAP).map((e, i) => {
              const otherId = end === "to" ? e.to : e.from;
              const o = index.byId.get(otherId);
              return (
                <li key={`${otherId}-${i}`}>
                  <code>{otherId}</code>{" "}
                  <span className="text-plane-muted">
                    ({o !== undefined ? o.stamp : "?"})
                  </span>{" "}
                  {o !== undefined ? oneLine(o.text, 110) : null}
                  {e.cite !== undefined && e.cite !== "" ? (
                    <span className="text-plane-muted"> [{e.cite}]</span>
                  ) : null}
                </li>
              );
            })}
            {list.length > LIST_CAP ? (
              <li className="text-plane-muted">
                … and {list.length - LIST_CAP} more {etype} edges — query a
                specific neighbor for its own card.
              </li>
            ) : null}
          </ul>
        </div>
      ))}
    </>
  );
}

/** The script's register lines: a row's state as the register carries it, or
 *  the untouched line for a contract/ path with no row. */
function RegisterLineList({
  lines,
}: {
  lines: readonly { path: string; row: GlassRegisterRow | null }[];
}) {
  return (
    <ul className="space-y-0.5 text-plane-muted">
      {lines.map(({ path, row }) => (
        <li key={path}>
          register: <code>{path}</code> —{" "}
          {row !== null ? (
            <>
              {row.state.toUpperCase()} ({row.verdict};{" "}
              {row.check.split(":")[0]}, checked {row.checked_on})
            </>
          ) : (
            <>
              NO ROW: untouched, do not trust
              (engineering/contract/verified.yaml)
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

function MetaField({
  label,
  value,
  extra,
}: {
  label: string;
  value: string;
  extra?: string;
}) {
  return (
    <div>
      <dt className="inline font-medium text-plane-muted">{label}: </dt>
      <dd className="inline">
        {value}
        {extra !== undefined ? (
          <span className="ml-3 text-plane-muted">{extra}</span>
        ) : null}
      </dd>
    </div>
  );
}
